import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Loader2, CheckCircle, AlertCircle, Shield } from 'lucide-react';

// Main Landing Page Component
const LandingPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isConnecting, setIsConnecting] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState(null);

    // Handle OAuth callback parameters when landing back on home page
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const message = params.get('message');
        const grantId = params.get('grantId');
        const email = params.get('email');
        const homeAccountId = params.get('homeAccountId');

        // Handle Email OAuth callback
        if (message === "OAuth successful" && grantId) {
            console.log('Email OAuth callback received on home page:', { message, grantId, email });

            // Clear the connecting state immediately
            setIsConnecting(false);

            // Save individual grant info
            if (email) {
                localStorage.setItem('grantId', grantId);
                localStorage.setItem('connectedEmail', email);

                // Show success notification
                setConnectionStatus({
                    type: 'success',
                    message: `Successfully connected ${email}!`
                });
            } else {
                // Show success notification even without email
                setConnectionStatus({
                    type: 'success',
                    message: 'Email account connected successfully!'
                });
            }

            // Clear the URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);

            // Clear success notification after 5 seconds
            setTimeout(() => setConnectionStatus(null), 8000);
        }

        // Handle Microsoft Graph OAuth callback
        if (message === "Microsoft Graph OAuth successful" && homeAccountId) {
            console.log('Microsoft Graph OAuth callback received on home page:', { message, homeAccountId, email });

            // Clear the authenticating state immediately
            setIsAuthenticating(false);

            // Save Microsoft Graph info
            if (email) {
                localStorage.setItem('homeAccountId', homeAccountId);
                localStorage.setItem('graphEmail', email);

                // Show success notification
                setConnectionStatus({
                    type: 'success',
                    message: `Successfully authenticated ${email}!`
                });
            } else {
                // Show success notification even without email
                setConnectionStatus({
                    type: 'success',
                    message: 'Microsoft Graph authenticated successfully!'
                });
            }

            // Clear the URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);

            // Clear success notification after 5 seconds
            setTimeout(() => setConnectionStatus(null), 8000);
        }

        // Handle navigation state (from OAuthCallback component redirect)
        if (location.state?.connectionSuccess) {
            // Clear the connecting state
            setIsConnecting(false);

            // Show success notification
            setConnectionStatus({
                type: 'success',
                message: location.state.connectedEmail
                    ? `Successfully connected ${location.state.connectedEmail}!`
                    : 'Email account connected successfully!'
            });

            // Clear navigation state
            navigate('/', { replace: true });

            // Clear success notification after 5 seconds
            setTimeout(() => setConnectionStatus(null), 8000);
        }

        if (location.state?.authenticationSuccess) {
            // Clear the authenticating state
            setIsAuthenticating(false);

            // Show success notification
            setConnectionStatus({
                type: 'success',
                message: location.state.authenticatedEmail
                    ? `Successfully authenticated ${location.state.authenticatedEmail}!`
                    : 'Microsoft Graph authenticated successfully!'
            });

            // Clear navigation state
            navigate('/', { replace: true });

            // Clear success notification after 5 seconds
            setTimeout(() => setConnectionStatus(null), 8000);
        }

        if (location.state?.connectionError || location.state?.authenticationError) {
            // Clear both connecting states
            setIsConnecting(false);
            setIsAuthenticating(false);

            // Show error notification
            setConnectionStatus({
                type: 'error',
                message: 'Authentication failed. Please try again.'
            });

            // Clear navigation state
            navigate('/', { replace: true });

            // Clear error notification after 5 seconds
            setTimeout(() => setConnectionStatus(null), 8000);
        }
    }, [location.search, location.state, navigate]);

    const handleConnectEmail = () => {
        // Prevent double-clicks
        if (isConnecting || isAuthenticating) return;

        setIsConnecting(true);
        
        // Clear connecting state after 5 seconds
        setTimeout(() => {
            setIsConnecting(false);
        }, 8000);

        // Redirect to Nylas OAuth endpoint
        window.location.href = `https://api-gateway.usesidecar.us/auth?redirect=${encodeURIComponent(window.location.origin)}`;
    };

    const handleAuthenticate = () => {
        // Prevent double-clicks
        if (isConnecting || isAuthenticating) return;

        setIsAuthenticating(true);
        
        // Clear authenticating state after 5 seconds
        setTimeout(() => {
            setIsAuthenticating(false);
        }, 8000);

        // Redirect to Microsoft Graph OAuth endpoint
        window.location.href = `https://api-gateway.usesidecar.us/graph-auth?redirect=${encodeURIComponent(window.location.origin)}`;
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center relative">
            {/* Notification Toast */}
            {connectionStatus && (
                <div className="notification-container">
                    <div className={`notification notification-${connectionStatus.type}`}>
                        <div className="notification-icon">
                            {connectionStatus.type === 'success' && <CheckCircle className="w-6 h-6" />}
                            {connectionStatus.type === 'error' && <AlertCircle className="w-6 h-6" />}
                            {connectionStatus.type === 'connecting' && <Loader2 className="w-6 h-6 animate-spin" />}
                            {connectionStatus.type === 'info' && <CheckCircle className="w-6 h-6" />}
                        </div>
                        <div className="notification-content">
                            <h3 className="notification-title">
                                {connectionStatus.type === 'success' && 'Success!'}
                                {connectionStatus.type === 'error' && 'Error'}
                                {connectionStatus.type === 'connecting' && 'Connecting'}
                                {connectionStatus.type === 'info' && 'Info'}
                            </h3>
                            <p className="notification-message">{connectionStatus.message}</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="container">
                <div className="max-w-md mx-auto text-center">
                    {/* Logo and Title */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            Welcome to Sidecar AI
                        </h2>
                        <p className="text-gray-600">
                            Connect your emails for seamless AI integration.
                        </p>
                    </div>

                    {/* Timeline Steps */}
                    <div className="timeline-container">
                        {/* Email Connection Step */}
                        <div className="timeline-step">
                            <div className="timeline-content">
                                <button
                                    onClick={handleConnectEmail}
                                    disabled={isConnecting || isAuthenticating}
                                    className="timeline-button"
                                >
                                    <div className="timeline-bubble">
                                        {isConnecting ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <Mail className="w-5 h-5" />
                                        )}
                                    </div>
                                    <span className="timeline-text">Connect Email</span>
                                </button>
                            </div>
                        </div>

                        {/* Authentication Step */}
                        <div className="timeline-step">
                            <div className="timeline-content">
                                <button
                                    onClick={handleAuthenticate}
                                    disabled={isConnecting || isAuthenticating}
                                    className="timeline-button"
                                >
                                    <div className="timeline-bubble">
                                        {isAuthenticating ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <Shield className="w-5 h-5" />
                                        )}
                                    </div>
                                    <span className="timeline-text">Authenticate</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <footer className="app-footer">
                <a
                    href="https://usesidecar.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Privacy Policy
                </a>
                <span className="app-footer-separator" aria-hidden="true">·</span>
                <a
                    href="https://usesidecar.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Terms of Service
                </a>
            </footer>
        </div>
    );
};

// OAuth Callback Handler Component
const OAuthCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState('processing');

    useEffect(() => {
        // Handle OAuth callback when user is redirected back
        const params = new URLSearchParams(location.search);
        const message = params.get('message');
        const grantId = params.get('grantId');
        const email = params.get('email');
        const homeAccountId = params.get('homeAccountId');

        console.log('OAuth callback received:', { message, grantId, email, homeAccountId });

        // Handle Email OAuth success
        if (message === "OAuth successful" && grantId && email) {
            // Save individual grant info
            localStorage.setItem('grantId', grantId);
            localStorage.setItem('connectedEmail', email);

            setStatus('success');

            // Redirect back to main page after showing success
            setTimeout(() => {
                navigate('/', {
                    state: {
                        connectionSuccess: true,
                        connectedEmail: email
                    }
                });
            }, 2000);
        }
        // Handle Microsoft Graph OAuth success
        else if (message === "Microsoft Graph OAuth successful" && homeAccountId && email) {
            // Save Microsoft Graph info
            localStorage.setItem('homeAccountId', homeAccountId);
            localStorage.setItem('graphEmail', email);

            setStatus('success');

            // Redirect back to main page after showing success
            setTimeout(() => {
                navigate('/', {
                    state: {
                        authenticationSuccess: true,
                        authenticatedEmail: email
                    }
                });
            }, 2000);
        }
        else {
            // Handle error case
            setStatus('error');

            // Redirect back to main page after showing error
            setTimeout(() => {
                navigate('/', {
                    state: {
                        connectionError: true,
                        authenticationError: true
                    }
                });
            }, 3000);
        }
    }, [location.search, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center py-12">
            <div className="container">
                <div className="card max-w-md mx-auto text-center animate-fade-in">
                    {status === 'processing' && (
                        <>
                            <div className="mb-8">
                                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient flex items-center justify-center">
                                    <Loader2 className="w-10 h-10 text-white animate-spin" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                                    Processing Authentication
                                </h2>
                                <p className="text-gray-600 leading-relaxed">
                                    Please wait while we securely establish your authentication...
                                </p>
                            </div>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="mb-8">
                                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient flex items-center justify-center">
                                    <CheckCircle className="w-10 h-10 text-white" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                                    Authentication Successful!
                                </h2>
                                <p className="text-gray-600 mb-6 leading-relaxed">
                                    Your account has been authenticated successfully and securely.
                                </p>
                                <p className="text-sm text-gray-500">
                                    Redirecting you back to the main dashboard...
                                </p>
                            </div>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="mb-8">
                                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient flex items-center justify-center">
                                    <AlertCircle className="w-10 h-10 text-white" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                                    Authentication Failed
                                </h2>
                                <p className="text-gray-600 mb-6 leading-relaxed">
                                    We encountered an issue while authenticating your account. Please try again.
                                </p>
                                <button
                                    onClick={() => navigate('/')}
                                    className="btn btn-primary"
                                >
                                    Return to Home
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// Main App Component with Routing
const App = () => {
    return (
        <Router>
            <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/callback" element={<OAuthCallback />} />
            </Routes>
        </Router>
    );
};

export default App; 
